import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import Grid from '@mui/material/Grid';

import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, TextField,
  DialogActions, Table, TableHead, TableBody, TableRow, TableCell,
  FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, FormControlLabel,
  FormHelperText
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
const apiURL = process.env.REACT_APP_API_URL || 'http://localhost:7071';

function PromotionDrawer({ onSubmit, onClose, initialData }) {
  const [form, setForm] = useState({
    promoName: '',
    description: '',
    discountType: 'percentage',
    discountValue: 0,
    roomTypes: [],
    startDate: '',
    endDate: '',
    minStay: 1,
    applicableDays: [],
    guestLimit: 0,
    visibility: 'public',
    promoCode: '',
    bookableStartDate: '',
    bookableEndDate: ''
  });

  useEffect(() => {
    if (initialData) {
      const user = JSON.parse(localStorage.getItem('user'));
      setForm({
        promoName: initialData.promoName || '',
        description: initialData.description || '',
        discountType: initialData.discountType || 'percentage',
        discountValue: initialData.discountValue || 0,
        roomTypes: initialData.roomTypes || [],
        startDate: initialData.startDate || '',
        endDate: initialData.endDate || '',
        minStay: initialData.minStay || 1,
        applicableDays: initialData.applicableDays || [],
        guestLimit: initialData.guestLimit || 0,
        visibility: initialData.visibility || 'public',
        promoCode: initialData.promoCode || '',
        bookableStartDate: initialData.bookableStartDate || '',
        bookableEndDate: initialData.bookableEndDate || '',
        propertyId:
          initialData.propertyId ||
          user?.propertyId?.id ||
          user?.properties?.[0]?.propertyId?.id ||
          ''
      });
    }
  }, [initialData]);

  const [roomTypesList, setRoomTypesList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchRoomTypes = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        const propertyId = user?.propertyId?.id || user?.properties?.[0]?.propertyId?.id;
        const res = await fetch(`${apiURL}/api/rooms?propertyId=${propertyId}`);
        const data = await res.json();
        setRoomTypesList(data);
      } catch (err) {
        console.error("Failed to fetch room types:", err);
      }
    };
    fetchRoomTypes();
  }, []);

  // Skeleton shimmer block for loading state
  const SkeletonBlock = ({ style = {} }) => (
    <motion.div
      initial={{ backgroundPositionX: '100%' }}
      animate={{ backgroundPositionX: '-100%' }}
      transition={{ repeat: Infinity, duration: 1.2, ease: 'linear' }}
      style={{
        width: '100%',
        height: '20px',
        borderRadius: '6px',
        backgroundImage: 'linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%)',
        backgroundSize: '200% 100%',
        backgroundRepeat: 'no-repeat',
        marginBottom: '12px',
        ...style
      }}
    />
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = initialData?._id
        ? `${apiURL}/api/promotions/${initialData._id}`
        : `${apiURL}/api/promotions`;
      const method = initialData?._id ? 'PUT' : 'POST';

      const user = JSON.parse(localStorage.getItem('user'));
      form.propertyId =
        form.propertyId ||
        user?.propertyId?.id ||
        user?.properties?.[0]?.propertyId?.id;

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      toast.success('🎉 Promotion created successfully!', {
        position: 'top-center',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
        theme: 'light',
      });

      // Trigger backend rate update for edits too
      const promoId = data._id || initialData?._id;
      if (promoId) {
        await fetch(`${apiURL}/api/rates/apply-promotion/${promoId}`, {
          method: 'POST'
        });
      }

      // Optional: Refresh rates if parent supports it (pass via props like onPromoCreated)
      if (typeof onSubmit === 'function') {
        onSubmit(); // parent can use this to trigger refetch
      }

    } catch (err) {
      console.error(err);
      alert('Error creating promotion');
    } finally {
      setLoading(false);
    }
  };

  if (roomTypesList.length === 0) {
    return (
      <Box sx={{ padding: 2 }}>
        <SkeletonBlock />
        <SkeletonBlock />
        <SkeletonBlock />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 2 }}>
      {onClose && (
        <Box display="flex" justifyContent="flex-end">
          <Button onClick={onClose} size="small">×</Button>
        </Box>
      )}
      <form onSubmit={handleSubmit}>
      <Grid container spacing={2} display="flex" flexWrap="wrap">
            <Grid sx={{ flexBasis: { xs: '100%', sm: '50%' }, flexGrow: 1, minWidth: 0 }}>
              <TextField
                fullWidth
                name="promoName"
                value={form.promoName}
                label="Promotion Name"
                margin="normal"
                onChange={handleChange}
              />
            </Grid>
            <Grid sx={{ flexBasis: { xs: '100%', sm: '50%' }, flexGrow: 1, minWidth: 0 }}>
              <TextField
                fullWidth
                name="description"
                value={form.description}
                label="Description"
                margin="normal"
                multiline
                rows={4}
                onChange={handleChange}
              />
            </Grid>

          <Grid sx={{ flexBasis: { xs: '100%', sm: '50%' }, flexGrow: 1, minWidth: 0 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Discount Type</InputLabel>
              <Select
                name="discountType"
                value={form.discountType}
                onChange={handleChange}
              >
                <MenuItem value="percentage">Percentage</MenuItem>
                <MenuItem value="fixed">Fixed</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid sx={{ flexBasis: { xs: '100%', sm: '50%' }, flexGrow: 1, minWidth: 0 }}>
            <FormControl fullWidth margin="normal">
              <TextField
                name="discountValue"
                type="number"
                value={form.discountValue}
                label="Discount Value"
                onChange={handleChange}
              />
              <FormHelperText>
                {form.discountType === 'percentage'
                  ? 'e.g., 20 means 20% off the room rate'
                  : 'e.g., 20 means $20 off the room rate'}
              </FormHelperText>
            </FormControl>
          </Grid>

          <Grid sx={{ flexBasis: { xs: '100%', sm: '50%' }, flexGrow: 1, minWidth: 0 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Room Types</InputLabel>
              <Select
                multiple
                name="roomTypes"
                value={form.roomTypes}
                onChange={(e) => setForm({ ...form, roomTypes: e.target.value })}
              >
                {roomTypesList.map((room) =>
                room && room.roomType ? (
                  <MenuItem key={room._id} value={room.roomType}>
                    {room.roomType}
                  </MenuItem>
                ) : null
              )}
              </Select>
            </FormControl>
          </Grid>

          <Grid sx={{ flexBasis: { xs: '100%', sm: '50%' }, flexGrow: 1, minWidth: 0 }}>
              <TextField
                fullWidth
                name="startDate"
                type="date"
                value={form.startDate?.split('T')[0] || ''}
                margin="normal"
                onChange={handleChange}
                label="Start Date"
                InputLabelProps={{ shrink: true }}
              />
          </Grid>

          <Grid sx={{ flexBasis: { xs: '100%', sm: '50%' }, flexGrow: 1, minWidth: 0 }}>
              <TextField
                fullWidth
                name="endDate"
                type="date"
                value={form.endDate?.split('T')[0] || ''}
                margin="normal"
                onChange={handleChange}
                label="End Date"
                InputLabelProps={{ shrink: true }}
              />
          </Grid>
          <Grid sx={{ flexBasis: { xs: '100%', sm: '50%' }, flexGrow: 1, minWidth: 0 }}>
            <TextField
              fullWidth
              name="bookableStartDate"
              type="date"
              value={form.bookableStartDate?.split('T')[0] || ''}
              margin="normal"
              onChange={handleChange}
              label="Bookable Start Date"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid sx={{ flexBasis: { xs: '100%', sm: '50%' }, flexGrow: 1, minWidth: 0 }}>
            <TextField
              fullWidth
              name="bookableEndDate"
              type="date"
              value={form.bookableEndDate?.split('T')[0] || ''}
              margin="normal"
              onChange={handleChange}
              label="Bookable End Date"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid sx={{ flexBasis: { xs: '100%', sm: '50%' }, flexGrow: 1, minWidth: 0 }}>
              <TextField
                fullWidth
                name="minStay"
                type="number"
                value={form.minStay}
                label="Minimum Stay"
                margin="normal"
                onChange={handleChange}
              />
          </Grid>

          <Grid sx={{ flexBasis: { xs: '100%', sm: '50%' }, flexGrow: 1, minWidth: 0 }}>
              <TextField
                fullWidth
                name="guestLimit"
                type="number"
                value={form.guestLimit}
                label="Guest Limit"
                margin="normal"
                onChange={handleChange}
              />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel shrink>Visibility</InputLabel>
              <Select
                name="visibility"
                value={form.visibility}
                onChange={handleChange}
              >
                <MenuItem value="public">Public</MenuItem>
                <MenuItem value="private">Private</MenuItem>
              </Select>
            </FormControl>
          </Grid>

        <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="promoCode"
                type="text"
                value={form.promoCode}
                label="Promo Code (optional)"
                margin="normal"
                onChange={handleChange}
              />
          </Grid>
          
        </Grid>
        <DialogActions>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Saving...' : initialData?._id ? 'Save Changes' : 'Create Promotion'}
          </Button>
        </DialogActions>
        {loading && (
          <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress size={24} />
          </Box>
        )}
      </form>
      <ToastContainer />
    </Box>
  );
}

function PromotionPanel() {
  const [promotions, setPromotions] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [roomTypeFilter, setRoomTypeFilter] = useState('');

  // Sidebar expand/collapse state
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const [selectedPromo, setSelectedPromo] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [roomTypesList, setRoomTypesList] = useState([]);
  useEffect(() => {
    const fetchRoomTypes = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        const propertyId = user?.propertyId?.id || user?.properties?.[0]?.propertyId?.id;
        const res = await fetch(`${apiURL}/api/rooms?propertyId=${propertyId}`);
        const data = await res.json();
        setRoomTypesList(data);
      } catch (err) {
        console.error("Failed to fetch room types for filter", err);
      }
    };
    fetchRoomTypes();
  }, []);

  const fetchPromotions = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    const propertyId = user?.propertyId?.id || user?.properties?.[0]?.propertyId?.id;
    fetch(`${apiURL}/api/promotions?propertyId=${propertyId}`)
      .then(res => res.json())
      .then(data => setPromotions(data))
      .catch(err => console.error('Failed to load promotions', err));
  };
  
  useEffect(() => {
    fetchPromotions();
  }, []);

  const filteredPromotions = (Array.isArray(promotions) ? promotions : []).filter(promo => {
    const matchStatus = statusFilter === '' || promo.status === statusFilter;
    const matchRoom = roomTypeFilter === '' || promo.roomTypes.includes(roomTypeFilter);
    return matchStatus && matchRoom;
  });

  const confirmDelete = (promo) => {
    toast(
      ({ closeToast }) => (
        <div>
          <p>Are you sure you want to delete <strong>{promo.promoName}</strong>?</p>
          <div>
            <button onClick={closeToast}>Cancel</button>
            <button
              style={{ background: 'crimson', color: '#fff' }}
              onClick={async () => {
                try {
                      const user = JSON.parse(localStorage.getItem('user'));
                      const propertyId = user?.propertyId?.id || user?.properties?.[0]?.propertyId?.id;
                      await fetch(`${apiURL}/api/promotions/${promo._id}`, {
                        method: 'DELETE',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ propertyId })
                      });
                  toast.success('Promotion deleted!');
                  setSelectedPromo(null);
                  setPromotions(prev => prev.filter(p => p._id !== promo._id));
                  closeToast();
                } catch (err) {
                  console.error(err);
                  toast.error('Failed to delete promotion.');
                }
              }}
            >
              Delete
            </button>
          </div>
        </div>
      ),
      {
        position: 'top-center',
        autoClose: false,
        closeOnClick: false,
        draggable: false
      }
    );
  };

  return (
    <div className="promotion-panel" style={{ display: 'flex' }}>
      <Button
        onClick={() => setSidebarOpen(prev => !prev)}
        sx={{
          position: 'absolute',
          top: 80,
          left: sidebarOpen ? 220 : 32,
          transform: 'translateX(-50%)',
          zIndex: 20,
          minWidth: 24,
          padding: 0,
          fontSize: '1.25rem',
          fontWeight: 'bold',
          background: '#fff',
          border: '1px solid #ccc',
          borderRadius: '50%',
          width: 28,
          height: 28,
          lineHeight: 1,
          '&:hover': {
            backgroundColor: '#f0f0f0'
          }
        }}
      >
        {sidebarOpen ? (
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#A58E63" viewBox="0 0 24 24">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#A58E63" viewBox="0 0 24 24">
            <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" />
          </svg>
        )}
      </Button>
      <aside
        className="sidebar"
        style={{
          background: '#f5f5f5',
          width: sidebarOpen ? 220 : 32,
          padding: sidebarOpen ? '16px 12px' : '16px 0px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          
          gap: '20px',
          height: '100%',
          borderRight: '1px solid #ddd',
          transition: 'all 0.3s ease'
        }}
      >
        {sidebarOpen && (
          <>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={e => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="upcoming">Upcoming</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Room Type</InputLabel>
              <Select
                value={roomTypeFilter}
                label="Room Type"
                onChange={e => setRoomTypeFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {roomTypesList.map((room) =>
                  room && room.roomType ? (
                    <MenuItem key={room._id} value={room.roomType}>
                      {room.roomType}
                    </MenuItem>
                  ) : null
                )}
              </Select>
            </FormControl>
          </>
        )}
      </aside>
      <main className="promotion-list" style={{ flex: 1 }}>
        <Button variant="contained" onClick={() => setShowDrawer(true)} sx={{ marginBottom: 2 }}>
          + New Promotion
        </Button>
        {filteredPromotions.length === 0 ? (
          <p>No promotions found.</p>
        ) : (
          <table className="promo-table" style={{ borderSpacing: '0 10px', width: '100%', textAlign: 'left' }}>
            <thead>
              <tr>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Promotion Name</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Discount</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Start Date</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>End Date</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredPromotions.map(promo => {
                const now = new Date();
                const bookableStart = new Date(promo.bookableStartDate);
                const bookableEnd = new Date(promo.bookableEndDate);

                let status = 'upcoming';
                if (now >= bookableStart && now <= bookableEnd) {
                  status = 'active';
                } else if (now > bookableEnd) {
                  status = 'expired';
                }

                const statusColor = status === 'active' ? 'green' : status === 'upcoming' ? 'goldenrod' : 'crimson';

                return (
                  <tr key={promo._id} onClick={() => {
                    setSelectedPromo(promo);
                    setShowDrawer(true);
                  }} style={{ cursor: 'pointer' }}>
                    <td style={{ padding: '8px 16px' }}><strong>{promo.promoName}</strong></td>
                    <td style={{ padding: '8px 16px' }}>
                      {promo.discountType === 'percentage' ? `${promo.discountValue}%` : `$${promo.discountValue}`}
                    </td>
                    <td style={{ padding: '8px 16px' }}>
                      {promo.startDate ? format(new Date(promo.startDate), 'dd/MMM/yyyy') : ''}
                    </td>
                    <td style={{ padding: '8px 16px' }}>
                      {promo.endDate ? format(new Date(promo.endDate), 'dd/MMM/yyyy') : ''}
                    </td>
                    <td style={{ padding: '8px 16px' }}>
                      <span style={{ color: statusColor }}>{status}</span>
                    </td>
                    <td style={{ padding: '8px 16px' }}>
                      <Button color="error" onClick={() => confirmDelete(promo)}>Delete</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </main>
      <Dialog open={showDrawer} onClose={() => setShowDrawer(false)} fullWidth maxWidth="sm">
        <DialogTitle>{selectedPromo ? 'Edit Promotion' : 'Create Promotion'}</DialogTitle>
        <DialogContent>
          <PromotionDrawer
            initialData={selectedPromo}
            onSubmit={() => {
              fetchPromotions();
              setShowDrawer(false);
              setSelectedPromo(null);
            }}
            onClose={() => setShowDrawer(false)}
          />
        </DialogContent>
      </Dialog>
      {/* {selectedPromo && (
        <div className="promotion-modal-overlay" onClick={() => setSelectedPromo(null)}>
          <div className="promotion-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{selectedPromo.promoName}</h3>
            <p>{selectedPromo.description}</p>
            <p><strong>Discount:</strong> {selectedPromo.discountValue} ({selectedPromo.discountType})</p>
            <p><strong>Room Types:</strong> {selectedPromo.roomTypes.join(', ')}</p>
            <p><strong>Start Date:</strong> {selectedPromo.startDate?.split('T')[0]}</p>
            <p><strong>End Date:</strong> {selectedPromo.endDate?.split('T')[0]}</p>
            <p><strong>Minimum Stay:</strong> {selectedPromo.minStay}</p>
            <p><strong>Guest Limit:</strong> {selectedPromo.guestLimit}</p>
            <p><strong>Visibility:</strong> {selectedPromo.visibility}</p>
            <p><strong>Promo Code:</strong> {selectedPromo.promoCode || '—'}</p>
            <button onClick={() => {
              setShowDrawer(true);
              setSelectedPromo(null);
            }}>Edit</button>
            <button onClick={() => confirmDelete(selectedPromo)}>
              Delete
            </button>
          </div>
        </div>
      )} */}
      <ToastContainer />
    </div>
  );
}

export default PromotionPanel;
